import { MouseEventHandler } from "react";
import { darken } from "../../lib/color_util";
import { useSelectTheme } from "../../store";

export type BtnColor =
  | "primary"
  | "black"
  | "warning"
  | "upgrade"
  | "disabled"
  | "grey_600"
  | "grey_400"
  | "grey_20";

export enum BtnPaddingSize {
  xl = "16px 32px",
  large = "12px 15px",
  medium = "8px 16px",
  mediumSmall = "6px 12px",
  small = "4px 8px",
}

export interface ButtonProps {
  disabled?: boolean;
  btnPadding?: keyof typeof BtnPaddingSize;
  btnColor?: BtnColor;
  textColor?: string;
  children?: string | JSX.Element;
  onClick?: MouseEventHandler;
  onMouseOver?: MouseEventHandler;
  onMouseOut?: MouseEventHandler;
  onMouseEnter?: MouseEventHandler;
  onMouseLeave?: MouseEventHandler;
  type?: "submit" | "button" | "reset";
  autoFocus?: boolean;
  css?: any;
  ref?: React.RefObject<HTMLButtonElement>;
}

/* a styled button */
export const Button = (props: ButtonProps) => {
  const { color, fontWeight } = useSelectTheme();
  const { disabled, btnPadding, btnColor, textColor, type, ref, ...rest } = props;

  const backgroundColor: { [key: string]: any } = {
    primary: color.primary,
    warning: color.warning,
    upgrade: color.upgrade,
    black: color.black,
    grey_600: color.grey_600,
    grey_400: color.grey_400,
    grey_20: color.grey_20,
  };

  const buttonPadding = btnPadding ? BtnPaddingSize[btnPadding] : "12px 15px";
  const buttonColor = btnColor ? backgroundColor[btnColor] : color.primary;

  return (
    <button
      {...rest}
      type={type || "submit"}
      disabled={disabled}
      ref={ref}
      css={{
        borderRadius: "5px",
        whiteSpace: "nowrap",
        border: "none",
        fontWeight: fontWeight.thick,
        color: textColor ? textColor : color.white,
        padding: buttonPadding,
        background: disabled ? color.surface.disabled : buttonColor,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        ":focus-visible": {
          outline: `2px solid ${color.primary}`,
          outlineOffset: "2px",
        },
        ":hover": {
          background: disabled ? color.surface.disabled : darken(buttonColor, 10),
        },
        ":active": {
          background: disabled ? color.surface.disabled : darken(buttonColor, 20),
        },
        cursor: disabled ? "auto" : "pointer",
      }}
    />
  );
};
